import React, { useState, useEffect } from 'react';
import { Gift, Star, Clock, CheckCircle } from 'lucide-react';
import { useRewards, useRedemptions } from '../../hooks/useRewards';
import { useSlotMachine } from '../../hooks/useSlotMachine';
import { getPublicAvailableRewards } from '../../services/rewardsService';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import SlotMachineAnimation from '../common/SlotMachineAnimation';

const CustomerRewards = ({ customer, company, isPublic = false }) => {
  const { getCustomerAvailableRewards, getPublicCustomerAvailableRewards } = useRewards(company?.id);
  const { requestNewRedemption, fetchCustomerRedemptions, redemptions, loading } = useRedemptions();
  const { animationData, showRewardAnimation, hideAnimation, resetAnimation } = useSlotMachine();
  
  const [availableRewards, setAvailableRewards] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [loadingRewards, setLoadingRewards] = useState(true);

  useEffect(() => {
    loadAvailableRewards();
    loadCustomerRedemptions();
  }, [customer, company]);

  const loadAvailableRewards = async () => {
    if (!customer || !company) return;
    
    setLoadingRewards(true);
    try {
      const customerPoints = customer.totalPoints || customer.points || 0;
      let rewards;
      if (isPublic) {
        // Usar função pública diretamente para evitar problemas de autenticação
        rewards = await getPublicAvailableRewards(company.id, customerPoints);
      } else {
        // Usar função autenticada
        rewards = await getCustomerAvailableRewards(customerPoints);
      }
      setAvailableRewards(rewards);
    } catch (error) {
      console.error('Erro ao carregar prêmios:', error);
    } finally {
      setLoadingRewards(false);
    }
  };

  const loadCustomerRedemptions = async () => {
    // Para clientes autenticados, precisamos do ID do registro do cliente na empresa específica
    const customerId = customer?.id || customer?.customerId;
    if (!customerId || isPublic) return; // Não carregar resgates no modo público
    
    try {
      await fetchCustomerRedemptions(customerId);
    } catch (error) {
      console.error('Erro ao carregar resgates:', error);
    }
  };

  const handleRedeemRequest = async () => {
    if (!selectedReward || !customer || !company) return;
    
    try {
      const oldPoints = customer.totalPoints || 0;
      const newPoints = oldPoints; // Em resgate, apenas registramos a solicitação, não descontamos ainda
      
      // Reset qualquer animação anterior
      resetAnimation();
      
      // Para modo público, precisamos criar o resgate diretamente
      if (isPublic) {
        // Usar o serviço diretamente para criar resgate público
        const { requestRedemption } = await import('../../services/rewardsService');
        await requestRedemption(
          customer.id, 
          company.id, 
          selectedReward.id, 
          customer.totalPoints || 0
        );
      } else {
        // Modo autenticado normal
        await requestNewRedemption(
          customer.id, 
          company.id, 
          selectedReward.id, 
          customer.totalPoints || 0
        );
      }
      
      // Aguardar um pouco antes de mostrar a animação
      setTimeout(() => {
        showRewardAnimation(oldPoints, newPoints, selectedReward.name, 2000);
      }, 100);
      
      setShowRedemptionModal(false);
      setSelectedReward(null);
      
      // Recarregar dados após a animação
      setTimeout(async () => {
        await loadAvailableRewards();
        if (!isPublic) {
          await loadCustomerRedemptions();
        }
      }, 2500);
      
    } catch (error) {
      alert('Erro ao solicitar resgate: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      approved: 'Aprovado',
      completed: 'Concluído',
      rejected: 'Rejeitado'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} />;
      case 'approved':
        return <Star size={16} />;
      case 'completed':
        return <CheckCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  if (loadingRewards) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Seção de Prêmios Disponíveis */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <Gift className="mr-2" />
          Prêmios Disponíveis
        </h2>
        
        {customer && (
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-blue-800">
              Você possui <span className="font-bold">{customer.totalPoints || customer.points || 0} pontos</span> na {company?.name}
            </p>
          </div>
        )}

        {availableRewards.length === 0 ? (
          <div className="text-center py-8">
            <Gift className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">Nenhum prêmio disponível no momento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableRewards.map((reward) => (
              <div
                key={reward.id}
                className={`border rounded-lg p-4 ${
                  reward.canRedeem 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                {/* Imagem do Prêmio */}
                {reward.imageUrl && (
                  <div className="mb-3">
                    <img
                      src={reward.imageUrl}
                      alt={reward.name}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                )}
                
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900">{reward.name}</h3>
                  <div className="flex flex-col items-end gap-1">
                    {reward.canRedeem && !reward.isOutOfStock && (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                        Disponível
                      </span>
                    )}
                    {reward.type === 'product' && reward.isOutOfStock && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                        Fora de estoque
                      </span>
                    )}
                    {reward.type === 'product' && reward.isLowStock && !reward.isOutOfStock && (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                        Estoque baixo
                      </span>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">{reward.description}</p>
                
                {/* Informações de estoque - apenas para produtos */}
                {reward.type === 'product' && reward.hasStockControl && (
                  <div className="text-xs text-gray-500 mb-2">
                    {reward.stockQuantity > 0 ? (
                      <span>📦 {reward.stockQuantity} em estoque</span>
                    ) : (
                      <span className="text-red-500">❌ Fora de estoque</span>
                    )}
                  </div>
                )}
                
                {reward.value && (
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Valor: {reward.value}
                  </p>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-blue-600">
                    {reward.pointsRequired} pontos
                  </span>
                  
                  {reward.canRedeem && !(reward.type === 'product' && reward.isOutOfStock) ? (
                    <button
                      onClick={() => {
                        setSelectedReward(reward);
                        setShowRedemptionModal(true);
                      }}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                    >
                      Resgatar
                    </button>
                  ) : reward.type === 'product' && reward.isOutOfStock ? (
                    <span className="text-sm text-red-500">
                      Fora de estoque
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">
                      Faltam {reward.pointsNeeded} pontos
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Seção de Histórico de Resgates - Só mostrar se não for público */}
      {!isPublic && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Meus Resgates
          </h2>
          
          {redemptions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Nenhum resgate realizado ainda</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prêmio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pontos Usados
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {redemptions.map((redemption) => (
                    <tr key={redemption.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {redemption.rewardName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {redemption.pointsUsed} pontos
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(redemption.status)}`}>
                          {getStatusIcon(redemption.status)}
                          {getStatusLabel(redemption.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {redemption.requestedAt?.toDate?.()?.toLocaleDateString('pt-BR') || 'Data não disponível'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Confirmação de Resgate */}
      <Modal
        isOpen={showRedemptionModal}
        onClose={() => {
          setShowRedemptionModal(false);
          setSelectedReward(null);
        }}
        title="Confirmar Resgate"
      >
        {selectedReward && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">
                {selectedReward.name}
              </h3>
              <p className="text-gray-600 mb-2">
                {selectedReward.description}
              </p>
              {selectedReward.value && (
                <p className="text-gray-700 font-medium">
                  Valor: {selectedReward.value}
                </p>
              )}
            </div>

            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <span className="text-blue-800">Pontos necessários:</span>
              <span className="font-bold text-blue-900">
                {selectedReward.pointsRequired} pontos
              </span>
            </div>

            <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
              <span className="text-green-800">Seus pontos atuais:</span>
              <span className="font-bold text-green-900">
                {customer?.totalPoints || customer?.points || 0} pontos
              </span>
            </div>

            {customer && (customer.totalPoints || customer.points || 0) >= selectedReward.pointsRequired && (
              <div className="flex justify-between items-center p-4 bg-yellow-50 rounded-lg">
                <span className="text-yellow-800">Pontos restantes após resgate:</span>
                <span className="font-bold text-yellow-900">
                  {(customer.totalPoints || customer.points || 0) - selectedReward.pointsRequired} pontos
                </span>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowRedemptionModal(false);
                  setSelectedReward(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                onClick={handleRedeemRequest}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Processando...' : 'Confirmar Resgate'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Animação de Caça-níquel */}
      <SlotMachineAnimation
        isVisible={animationData.isVisible}
        onClose={hideAnimation}
        type={animationData.type}
        oldValue={animationData.oldValue}
        newValue={animationData.newValue}
        rewardName={animationData.rewardName}
        duration={animationData.duration}
      />
    </div>
  );
};

export default CustomerRewards;
